import { disconnectAll, initSockets } from "@/lib/socketManager";
import { isAuthenticatedAtom } from "@/store/authAtoms";
import { socketsAtom } from "@/store/socketsAtom";
import type { SocketMap } from "@/store/socketsAtom";
import { useAtom, useAtomValue } from "jotai";
import { useEffect } from "react";

/**
 * Establishes socket connections once the user is authenticated and
 * tears them down on logout. Automatically reconnects on token refresh.
 */
export function useInitializeSockets() {
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  const [socketMap, setSocketMap] = useAtom(socketsAtom);

  // Establish connections when the token is present and we have no sockets yet.
  useEffect(() => {
    if (!isAuthenticated) return;

    // Pull the access token from storage (same source used throughout the app).
    const token = localStorage.getItem("access_token");
    if (!token) return;

    // Connect (no-op if already connected)
    const map: SocketMap = initSockets(token);
    if (socketMap !== map) {
      setSocketMap(map);
    }
  }, [isAuthenticated, setSocketMap, socketMap]);

  // Tear down sockets on logout.
  useEffect(() => {
    if (isAuthenticated) return;

    disconnectAll();
    if (socketMap !== null) {
      setSocketMap(null);
    }
  }, [isAuthenticated, setSocketMap, socketMap]);
}
